import React, { createContext, useContext, useEffect, useRef, useCallback, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

interface SpeechRecognitionContextType {
  recognition: any;
  isListening: number;
  startListening: () => void;
  stopListening: () => void;
}

const SpeechRecognitionContext = createContext<SpeechRecognitionContextType | null>(null);

export const useSpeechRecognition = () => {
  const context = useContext(SpeechRecognitionContext);
  if (!context) {
    throw new Error('useSpeechRecognition must be used within a SpeechRecognitionProvider');
  }
  return context;
};

interface SpeechRecognitionProviderProps {
  children: ReactNode;
}

export const SpeechRecognitionProvider: React.FC<SpeechRecognitionProviderProps> = ({ children }) => {
  const recognitionRef = useRef<any>(null);
  const isListeningRef = useRef(0);
  const SpeechRecognition = useRef((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition);
  const location = useLocation();
  const currentPathRef = useRef(location.pathname);
  const isMobile = useRef(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent));

  console.log(`SpeechRecognitionProvider rendering at location: ${location.pathname}`);

  // Monitor location changes
  useEffect(() => {
    const oldPath = currentPathRef.current;
    currentPathRef.current = location.pathname;
    
    // If we're navigating away from the player page, stop listening
    if (oldPath.endsWith('player') && !location.pathname.endsWith('player')) {
      console.log('Navigating away from player, stopping speech recognition');
      if (recognitionRef.current && isListeningRef.current > 0) {
        stopListening();
      }
    }
  }, [location]);

  // Initialize the SpeechRecognition object
  const initializeListener = useCallback(() => {
    console.log("Initializing new SpeechRecognition...");
    if (!('SpeechRecognition' in window || 'webkitSpeechRecognition' in window)) {
      console.error('SpeechRecognition is not supported on this device.');
      return;
    }

    if (!recognitionRef.current) {
      recognitionRef.current = new SpeechRecognition.current();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';

      if (isMobile.current) {
        recognitionRef.current.continuous = false;
        recognitionRef.current.maxAlternatives = 3;
      }

      recognitionRef.current.onstart = () => {
        console.log(`Speech recognition started. isListening promoted from: ${isListeningRef.current} to 2`);
        isListeningRef.current = 2;
      };

      recognitionRef.current.onend = () => {
        console.log(`onend: Speech recognition ended with isListening: ${isListeningRef.current}, location: ${currentPathRef.current}`);
        if (isListeningRef.current === 2 && currentPathRef.current.endsWith('player')) {
          console.log('Speech recognition timed out, restarting...');
          isListeningRef.current = 0;
          if (isMobile.current) {
            setTimeout(() => startListening(), 300);
          } else {
            startListening();
          }
        } else {
          console.log(`listener onend: based on state (isListening and location) assume we want Speech Recognition OFF`);
          isListeningRef.current = 0;
        }
      };

      recognitionRef.current.onerror = (event: any) => {
        console.warn(`Speech recognition error: ${event.error}`);

        if (event.error === 'no-speech') {
          console.log('Ignoring no-speech error');
        } else if (event.error === 'aborted') {
          console.warn('Speech recognition aborted. Re-initializing...');
          delete recognitionRef.current.onstart;
          delete recognitionRef.current.onend;
          delete recognitionRef.current.onresult;
          delete recognitionRef.current;
          initializeListener();
        }
      };

      // Initialize with empty onresult handler
      recognitionRef.current.onresult = () => {};
    }
  }, []);

  // Setup SpeechRecognition on mount
  useEffect(() => {
    if (navigator.mediaDevices?.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ audio: true })
        .then(() => {
          if (SpeechRecognition.current) {
            startListening();
          }
        })
        .catch(err => console.error("Microphone access error: ", err));
    }

    return () => {
      if (recognitionRef.current) {
        console.log(`Stopping speech recognition on unmount`);
      }
    };
  }, []);

  const startListening = useCallback(() => {
    if (!recognitionRef.current) {
      console.log("Recognition is not initialized yet.");
      initializeListener();
      if (!recognitionRef.current) {
        console.log(`Failed to create listener, so not starting`);
        isListeningRef.current = 0;
        return;
      }
    }

    if (isListeningRef.current === 0) {
      if (recognitionRef.current) {
        if (isMobile.current) {
          setTimeout(() => { recognitionRef.current.start(); }, 200);
        } else {
          recognitionRef.current.start();
        }

        isListeningRef.current = 1;
        console.log(`Start listening requested. Current state: 0 new state: ${isListeningRef.current}`);
      } else {
        console.error("SpeechRecognition object lost validity");
      }
    } else if (isListeningRef.current === 2) {
      console.log("startListening called but already listening (state 2) so ignore");
    } else {
      console.log(`startListening requested ignored bc already in startup window state: ${isListeningRef.current}`);
    }
  }, [initializeListener]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      try {
        console.log("Stopping speech recognition... set isListeningRef to 0");
        recognitionRef.current.stop();
        isListeningRef.current = 0;
      } catch (e) {
        console.error("Error stopping speech recognition:", e);
      }
    } else {
      console.log("Stop requested but wasn't listening.");
    }
  }, []);

  return (
    <SpeechRecognitionContext.Provider
      value={{
        recognition: recognitionRef.current,
        isListening: isListeningRef.current,
        startListening,
        stopListening
      }}
    >
      {children}
    </SpeechRecognitionContext.Provider>
  );
};